import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, AlertCircle, CheckCircle, Edit3, Plus, Trash2, ArrowRight, Upload, Eye, FileText } from 'lucide-react';
import {
  ChangeableField,
  FIELD_LABELS,
  STAFF_CHANGEABLE_FIELDS,
  PROFESSOR_CHANGEABLE_FIELDS,
  createChangeRequest,
  getMyChangeRequests,
  type ChangeRequest,
  type CreateChangeRequestDto,
} from '@/services/changeRequestService';
import {
  fetchDepartements,
  fetchGrades,
  fetchDiplomes,
  fetchCountries,
  type NomenclatureDepartement,
  type NomenclatureGrade,
  type NomenclatureDiplome,
  type NomenclatureCountry,
} from '@/services/nomenclatureService';
import { uploadDocument, type StudentDocumentType } from '@/services/documentsService';
import { useAuth } from '@/features/auth/hooks/useAuth';

// Map ChangeableField document types to StudentDocumentType
const documentFieldToType: Record<string, StudentDocumentType> = {
  [ChangeableField.DOCUMENT_CIN_FRONT]: 'CIN_FRONT',
  [ChangeableField.DOCUMENT_CIN_BACK]: 'CIN_BACK',
  [ChangeableField.DOCUMENT_PASSPORT]: 'PASSPORT',
  [ChangeableField.DOCUMENT_CV]: 'CV',
  [ChangeableField.DOCUMENT_DIPLOME]: 'DIPLOME_SCAN',
  [ChangeableField.DOCUMENT_PHOTO]: 'PHOTO',
  [ChangeableField.DOCUMENT_RIB]: 'ATTESTATION_RIB',
};

interface StaffChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffData: Record<string, unknown>;
  isProfessor?: boolean;
  onSuccess?: () => void;
}

// Queued change item
interface QueuedChange {
  id: string;
  fieldName: ChangeableField;
  currentValue: string;
  newValue: string;
  reason?: string;
}

// Mapping from ChangeableField to staffData keys
const fieldToDataKey: Record<string, string> = {
  [ChangeableField.FIRST_NAME]: 'prenom',
  [ChangeableField.LAST_NAME]: 'nom',
  [ChangeableField.FIRST_NAME_ARABIC]: 'prenomArabe',
  [ChangeableField.LAST_NAME_ARABIC]: 'nomArabe',
  [ChangeableField.PHONE]: 'telephone',
  [ChangeableField.ADDRESS]: 'adresse',
  [ChangeableField.CITY]: 'ville',
  [ChangeableField.POSTAL_CODE]: 'codePostal',
  [ChangeableField.EMERGENCY_NAME]: 'urgenceNom',
  [ChangeableField.EMERGENCY_PHONE]: 'urgenceNumero',
  [ChangeableField.EMERGENCY_ADDRESS]: 'urgenceAdresse',
  [ChangeableField.EMERGENCY_RELATION]: 'urgenceRelation',
  [ChangeableField.STAFF_CNIE]: 'cnie',
  [ChangeableField.STAFF_PASSPORT]: 'passport',
  [ChangeableField.STAFF_DATE_OF_BIRTH]: 'dateNaissance',
  [ChangeableField.STAFF_NATIONALITY]: 'paysOrigine',
  [ChangeableField.STAFF_GENDER]: 'sexe',
  [ChangeableField.STAFF_HANDICAP]: 'handicap',
  [ChangeableField.STAFF_RIB]: 'rib',
  [ChangeableField.STAFF_SALARY]: 'salaireMensuelNet',
  [ChangeableField.STAFF_FONCTION]: 'fonction',
  [ChangeableField.STAFF_DIPLOME]: 'diplomeId',
  [ChangeableField.STAFF_DATE_RECRUTEMENT]: 'dateRecrutement',
  [ChangeableField.STAFF_STATUT_PROFESSEUR]: 'statutProfesseur',
  [ChangeableField.STAFF_DEPARTEMENT]: 'departementAttacheId',
  [ChangeableField.STAFF_EST_PUBLIC]: 'estOriginaireEtablissementPublic',
  [ChangeableField.STAFF_ETABLISSEMENT_ORIGINE]: 'etablissementOrigine',
  [ChangeableField.STAFF_GRADE]: 'gradeId',
  [ChangeableField.STAFF_SPECIALITE]: 'specialite',
  [ChangeableField.STAFF_MATIERES]: 'matieresEnseignees',
  [ChangeableField.STAFF_TAUX_HORAIRE]: 'tauxHoraire',
  // Document fields - these map to document types, not profile fields
  [ChangeableField.DOCUMENT_CIN_FRONT]: 'documentCinFront',
  [ChangeableField.DOCUMENT_CIN_BACK]: 'documentCinBack',
  [ChangeableField.DOCUMENT_PASSPORT]: 'documentPassport',
  [ChangeableField.DOCUMENT_CV]: 'documentCv',
  [ChangeableField.DOCUMENT_DIPLOME]: 'documentDiplome',
  [ChangeableField.DOCUMENT_PHOTO]: 'documentPhoto',
  [ChangeableField.DOCUMENT_RIB]: 'documentRib',
};

// Define field input types
type FieldInputType = 'text' | 'email' | 'tel' | 'date' | 'number' | 'select' | 'nomenclature' | 'arabic' | 'phone_with_country' | 'document';

interface FieldConfig {
  inputType: FieldInputType;
  nomenclatureType?: 'departements' | 'grades' | 'diplomes' | 'countries';
  options?: { value: string; label: string }[];
  placeholder?: string;
  min?: number;
  max?: number;
  phoneCountryField?: string; // Field name for the phone country code
}

// Static options for simple select fields
const genderOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' },
];

const handicapOptions = [
  { value: 'N', label: 'Aucun' },
  { value: 'A', label: 'Auditif' },
  { value: 'M', label: 'Moteur' },
  { value: 'V', label: 'Visuel' },
  { value: 'AM', label: 'Auditif et Moteur' },
  { value: 'AV', label: 'Auditif et Visuel' },
  { value: 'MV', label: 'Moteur et Visuel' },
  { value: 'T', label: 'Tous' },
  { value: 'XX', label: 'Autre' },
];

const statutProfesseurOptions = [
  { value: 'PERMANENT', label: 'Permanent' },
  { value: 'VACATAIRE', label: 'Vacataire' },
];

const estPublicOptions = [
  { value: 'true', label: 'Oui' },
  { value: 'false', label: 'Non' },
];

// Emergency contact relationship options (matching ProfileForm)
const relationshipOptions = [
  { value: 'parent', label: 'Parent' },
  { value: 'spouse', label: 'Conjoint(e)' },
  { value: 'sibling', label: 'Frère/Sœur' },
  { value: 'child', label: 'Enfant' },
  { value: 'friend', label: 'Ami(e)' },
  { value: 'colleague', label: 'Collègue' },
  { value: 'other', label: 'Autre' },
];

// Country phone codes for phone input
const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'SA', name: 'Arabie Saoudite', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'Émirats Arabes Unis', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Égypte', dialCode: '+20', flag: '🇪🇬' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: 'Côte d\'Ivoire', dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222', flag: '🇲🇷' },
];

// Field configurations - determines what input type to use for each field
const fieldConfigs: Partial<Record<ChangeableField, FieldConfig>> = {
  // Text fields
  [ChangeableField.FIRST_NAME]: { inputType: 'text', placeholder: 'Prénom' },
  [ChangeableField.LAST_NAME]: { inputType: 'text', placeholder: 'Nom' },
  // Arabic name fields - use special arabic input type
  [ChangeableField.FIRST_NAME_ARABIC]: { inputType: 'arabic', placeholder: 'الاسم الشخصي' },
  [ChangeableField.LAST_NAME_ARABIC]: { inputType: 'arabic', placeholder: 'الاسم العائلي' },
  [ChangeableField.ADDRESS]: { inputType: 'text', placeholder: 'Adresse complète' },
  [ChangeableField.CITY]: { inputType: 'text', placeholder: 'Ville' },
  [ChangeableField.POSTAL_CODE]: { inputType: 'text', placeholder: 'Code postal' },
  [ChangeableField.EMERGENCY_NAME]: { inputType: 'text', placeholder: 'Nom du contact' },
  [ChangeableField.EMERGENCY_ADDRESS]: { inputType: 'text', placeholder: 'Adresse' },
  [ChangeableField.EMERGENCY_RELATION]: { inputType: 'select', options: relationshipOptions },
  [ChangeableField.STAFF_CNIE]: { inputType: 'text', placeholder: 'Ex: AB123456' },
  [ChangeableField.STAFF_PASSPORT]: { inputType: 'text', placeholder: 'Numéro de passeport' },
  [ChangeableField.STAFF_RIB]: { inputType: 'text', placeholder: 'RIB bancaire (24 chiffres)' },
  [ChangeableField.STAFF_FONCTION]: { inputType: 'text', placeholder: 'Ex: Enseignant, Administratif...' },
  [ChangeableField.STAFF_ETABLISSEMENT_ORIGINE]: { inputType: 'text', placeholder: 'Nom de l\'établissement' },
  [ChangeableField.STAFF_SPECIALITE]: { inputType: 'text', placeholder: 'Ex: Marketing, Finance...' },
  [ChangeableField.STAFF_MATIERES]: { inputType: 'text', placeholder: 'Ex: Marketing, Comptabilité...' },

  // Phone fields with country selector
  [ChangeableField.PHONE]: { inputType: 'phone_with_country', placeholder: '6XX XXX XXX', phoneCountryField: 'phoneCountry' },
  [ChangeableField.EMERGENCY_PHONE]: { inputType: 'phone_with_country', placeholder: '6XX XXX XXX', phoneCountryField: 'urgencePhoneCountry' },

  // Date fields
  [ChangeableField.STAFF_DATE_OF_BIRTH]: { inputType: 'date' },
  [ChangeableField.STAFF_DATE_RECRUTEMENT]: { inputType: 'date' },

  // Number fields
  [ChangeableField.STAFF_SALARY]: { inputType: 'number', min: 0, placeholder: 'Montant en DH' },
  [ChangeableField.STAFF_TAUX_HORAIRE]: { inputType: 'number', min: 0, placeholder: 'Taux en DH/heure' },

  // Simple select fields (static options)
  [ChangeableField.STAFF_GENDER]: { inputType: 'select', options: genderOptions },
  [ChangeableField.STAFF_HANDICAP]: { inputType: 'select', options: handicapOptions },
  [ChangeableField.STAFF_STATUT_PROFESSEUR]: { inputType: 'select', options: statutProfesseurOptions },
  [ChangeableField.STAFF_EST_PUBLIC]: { inputType: 'select', options: estPublicOptions },

  // Nomenclature-based select fields (loaded from API)
  [ChangeableField.STAFF_DEPARTEMENT]: { inputType: 'nomenclature', nomenclatureType: 'departements' },
  [ChangeableField.STAFF_GRADE]: { inputType: 'nomenclature', nomenclatureType: 'grades' },
  [ChangeableField.STAFF_DIPLOME]: { inputType: 'nomenclature', nomenclatureType: 'diplomes' },
  [ChangeableField.STAFF_NATIONALITY]: { inputType: 'nomenclature', nomenclatureType: 'countries' },

  // Document fields - uses textarea for explaining the modification request
  [ChangeableField.DOCUMENT_CIN_FRONT]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre CIN (Recto)' },
  [ChangeableField.DOCUMENT_CIN_BACK]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre CIN (Verso)' },
  [ChangeableField.DOCUMENT_PASSPORT]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre passeport' },
  [ChangeableField.DOCUMENT_CV]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre CV' },
  [ChangeableField.DOCUMENT_DIPLOME]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre diplôme' },
  [ChangeableField.DOCUMENT_PHOTO]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre photo d\'identité' },
  [ChangeableField.DOCUMENT_RIB]: { inputType: 'document', placeholder: 'Décrivez les modifications à apporter à votre RIB' },
};

// Group fields for organized display
const staffFieldGroups = [
  {
    title: 'Identité',
    fields: [
      ChangeableField.FIRST_NAME,
      ChangeableField.LAST_NAME,
      ChangeableField.FIRST_NAME_ARABIC,
      ChangeableField.LAST_NAME_ARABIC,
      ChangeableField.STAFF_CNIE,
      ChangeableField.STAFF_PASSPORT,
      ChangeableField.STAFF_DATE_OF_BIRTH,
      ChangeableField.STAFF_NATIONALITY,
      ChangeableField.STAFF_GENDER,
      ChangeableField.STAFF_HANDICAP,
    ],
  },
  {
    title: 'Coordonnées',
    fields: [
      ChangeableField.PHONE,
      ChangeableField.ADDRESS,
    ],
  },
  {
    title: 'Contact d\'urgence',
    fields: [
      ChangeableField.EMERGENCY_NAME,
      ChangeableField.EMERGENCY_PHONE,
      ChangeableField.EMERGENCY_ADDRESS,
      ChangeableField.EMERGENCY_RELATION,
    ],
  },
  {
    title: 'Informations Professionnelles',
    fields: [
      ChangeableField.STAFF_FONCTION,
      ChangeableField.STAFF_DIPLOME,
      ChangeableField.STAFF_DATE_RECRUTEMENT,
      ChangeableField.STAFF_RIB,
      ChangeableField.STAFF_SALARY,
    ],
  },
  {
    title: 'Documents',
    fields: [
      ChangeableField.DOCUMENT_CIN_FRONT,
      ChangeableField.DOCUMENT_CIN_BACK,
      ChangeableField.DOCUMENT_PASSPORT,
      ChangeableField.DOCUMENT_CV,
      ChangeableField.DOCUMENT_DIPLOME,
      ChangeableField.DOCUMENT_PHOTO,
      ChangeableField.DOCUMENT_RIB,
    ],
  },
];

// Additional field groups for professors
const professorFieldGroups = [
  {
    title: 'Informations Enseignant',
    fields: [
      ChangeableField.STAFF_STATUT_PROFESSEUR,
      ChangeableField.STAFF_DEPARTEMENT,
      ChangeableField.STAFF_GRADE,
      ChangeableField.STAFF_SPECIALITE,
      // STAFF_MATIERES excluded: system-generated from course assignments
      ChangeableField.STAFF_TAUX_HORAIRE,
      ChangeableField.STAFF_EST_PUBLIC,
      ChangeableField.STAFF_ETABLISSEMENT_ORIGINE,
    ],
  },
];

export const StaffChangeRequestModal: React.FC<StaffChangeRequestModalProps> = ({
  isOpen,
  onClose,
  staffData,
  isProfessor = false,
  onSuccess,
}) => {
  const { tokens } = useAuth();

  // Steps: 'select' -> 'form' -> 'review' -> 'success'
  const [step, setStep] = useState<'select' | 'form' | 'review' | 'success'>('select');
  const [selectedField, setSelectedField] = useState<ChangeableField | null>(null);
  const [newValue, setNewValue] = useState('');
  const [reason, setReason] = useState('');
  const [, setIsLoading] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isLoadingNomenclature, setIsLoadingNomenclature] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<ChangeRequest[]>([]);

  // Queue for multiple changes
  const [queuedChanges, setQueuedChanges] = useState<QueuedChange[]>([]);
  const [submissionProgress, setSubmissionProgress] = useState({ current: 0, total: 0 });
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);

  // Nomenclature data
  const [departements, setDepartements] = useState<NomenclatureDepartement[]>([]);
  const [grades, setGrades] = useState<NomenclatureGrade[]>([]);
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  const [countries, setCountries] = useState<NomenclatureCountry[]>([]);

  // Phone country code state for phone fields
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState('MA');

  // Document upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get applicable fields based on role
  const applicableFields = isProfessor ? PROFESSOR_CHANGEABLE_FIELDS : STAFF_CHANGEABLE_FIELDS;
  const fieldGroups = isProfessor ? [...staffFieldGroups, ...professorFieldGroups] : staffFieldGroups;

  // Load existing pending requests
  useEffect(() => {
    if (isOpen) {
      loadPendingRequests();
    }
  }, [isOpen]);

  const loadPendingRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const requests = await getMyChangeRequests();
      setPendingRequests(requests.filter(r => r.status === 'PENDING'));
    } catch (err) {
      console.error('Error loading pending requests:', err);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Load nomenclature data when needed
  const loadNomenclatureData = async (type: string) => {
    setIsLoadingNomenclature(true);
    try {
      switch (type) {
        case 'departements':
          if (departements.length === 0 && tokens?.accessToken) {
            const data = await fetchDepartements(tokens.accessToken);
            setDepartements(data || []);
          }
          break;
        case 'grades':
          if (grades.length === 0) {
            const data = await fetchGrades();
            setGrades(data || []);
          }
          break;
        case 'diplomes':
          if (diplomes.length === 0) {
            const data = await fetchDiplomes();
            setDiplomes(data || []);
          }
          break;
        case 'countries':
          if (countries.length === 0) {
            const data = await fetchCountries();
            setCountries(data || []);
          }
          break;
      }
    } catch (err) {
      console.error('Error loading nomenclature:', err);
    } finally {
      setIsLoadingNomenclature(false);
    }
  };

  const getCurrentValue = (field: ChangeableField): string => {
    const config = fieldConfigs[field];
    // For document fields, there's no "current value" - just return empty
    if (config?.inputType === 'document') {
      return '';
    }

    const key = fieldToDataKey[field];
    const value = staffData[key];
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const getDisplayValue = (field: ChangeableField, value: string): string => {
    const config = fieldConfigs[field];

    // For document fields, show a different message
    if (config?.inputType === 'document') {
      if (!value) return '(Document actuel)';
      // Check if it's a document update with URL
      if (value.startsWith('DOCUMENT_UPDATE:')) {
        return '(Nouveau document)';
      }
      // For queued changes, truncate long descriptions
      if (value.length > 50) return value.substring(0, 47) + '...';
      return value;
    }

    if (!value) return '(non renseigné)';

    if (!config) return value;

    // For select fields, show the label instead of the value
    if (config.inputType === 'select' && config.options) {
      const option = config.options.find(o => o.value === value);
      return option?.label || value;
    }

    // For nomenclature fields, try to get the label
    if (config.inputType === 'nomenclature') {
      switch (config.nomenclatureType) {
        case 'departements': {
          const dept = departements.find(d => d.id === value);
          return dept?.labelFr || value;
        }
        case 'grades': {
          const grade = grades.find(g => g.id === value);
          return grade?.labelFr || value;
        }
        case 'diplomes': {
          const diplome = diplomes.find(d => d.id === value || d.labelFr === value);
          return diplome?.labelFr || value;
        }
        case 'countries': {
          const country = countries.find(c => c.code === value || c.nameFr === value);
          return country?.nameFr || value;
        }
      }
    }

    return value;
  };

  const hasPendingRequest = (field: ChangeableField): boolean => {
    return pendingRequests.some(r => r.fieldName === field);
  };

  const isFieldQueued = (field: ChangeableField): boolean => {
    return queuedChanges.some(q => q.fieldName === field);
  };

  const handleFieldSelect = async (field: ChangeableField) => {
    if (hasPendingRequest(field) || isFieldQueued(field)) return;

    setSelectedField(field);
    setNewValue(getCurrentValue(field));
    setReason('');
    setError(null);

    // Load nomenclature data if needed
    const config = fieldConfigs[field];
    if (config?.inputType === 'nomenclature' && config.nomenclatureType) {
      await loadNomenclatureData(config.nomenclatureType);
    }

    // Initialize phone country code from current data if it's a phone field
    if (config?.inputType === 'phone_with_country' && config.phoneCountryField) {
      const currentCountry = staffData[config.phoneCountryField] as string;
      setSelectedPhoneCountry(currentCountry || 'MA');
    }

    setStep('form');
  };

  const handleAddToQueue = async () => {
    if (!selectedField) return;

    const currentValue = getCurrentValue(selectedField);
    const config = fieldConfigs[selectedField];

    // For phone fields, include the country code in the value
    let finalNewValue = newValue.trim();
    let displayCurrentValue = currentValue;

    if (config?.inputType === 'phone_with_country') {
      const selectedCountryData = COUNTRY_PHONE_CODES.find(c => c.code === selectedPhoneCountry);
      const dialCode = selectedCountryData?.dialCode || '+212';
      finalNewValue = `${selectedPhoneCountry}:${newValue.trim()}`; // Format: "MA:0612345678"

      // Get current phone country for display
      if (config.phoneCountryField) {
        const currentCountry = staffData[config.phoneCountryField] as string;
        const currentCountryData = COUNTRY_PHONE_CODES.find(c => c.code === currentCountry);
        if (currentCountryData && currentValue) {
          displayCurrentValue = `${currentCountryData.dialCode} ${currentValue}`;
        }
      }
      finalNewValue = `${dialCode} ${newValue.trim()}`; // Format for display: "+212 0612345678"
    }

    // For document fields, upload the file first
    if (config?.inputType === 'document') {
      if (!selectedFile) {
        setError('Veuillez sélectionner un fichier à télécharger');
        return;
      }

      setIsUploadingDocument(true);
      setError(null);

      try {
        const documentType = documentFieldToType[selectedField];
        if (!documentType) {
          throw new Error('Type de document non reconnu');
        }

        // Upload the document
        const uploadedDoc = await uploadDocument(documentType, selectedFile);

        // Use the file URL as the new value (this will be stored in the change request)
        finalNewValue = `DOCUMENT_UPDATE:${uploadedDoc.fileUrl}`;
        displayCurrentValue = '(Document actuel)';

        // Add reason if provided
        if (reason.trim()) {
          finalNewValue += `|REASON:${reason.trim()}`;
        }
      } catch (uploadError) {
        console.error('Document upload error:', uploadError);
        setError(uploadError instanceof Error ? uploadError.message : 'Erreur lors du téléchargement du document');
        setIsUploadingDocument(false);
        return;
      }

      setIsUploadingDocument(false);
    } else {
      // Skip "different value" validation for documents - they just need a file
      // Normalize values for comparison (trim whitespace, case-insensitive)
      const normalizedCurrent = (currentValue ?? '').toString().trim().toLowerCase();
      const normalizedNew = (newValue ?? '').toString().trim().toLowerCase();

      if (normalizedNew === normalizedCurrent) {
        setError('La nouvelle valeur doit être différente de la valeur actuelle');
        return;
      }

      if (!newValue.trim()) {
        setError('La nouvelle valeur ne peut pas être vide');
        return;
      }
    }

    // Add to queue
    const newChange: QueuedChange = {
      id: `${selectedField}-${Date.now()}`,
      fieldName: selectedField,
      currentValue: displayCurrentValue,
      newValue: finalNewValue,
      reason: reason.trim() || undefined,
    };

    setQueuedChanges(prev => [...prev, newChange]);

    // Reset form and go back to select
    resetFormState();
    setStep('select');
  };

  // Helper function to reset form state
  const resetFormState = () => {
    setSelectedField(null);
    setNewValue('');
    setReason('');
    setError(null);
    setSelectedPhoneCountry('MA');
    setSelectedFile(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
  };

  const handleRemoveFromQueue = (id: string) => {
    setQueuedChanges(prev => prev.filter(q => q.id !== id));
  };

  const handleSubmitAll = async () => {
    if (queuedChanges.length === 0) return;

    setIsLoading(true);
    setError(null);
    setSubmissionErrors([]);
    setSubmissionProgress({ current: 0, total: queuedChanges.length });
    setStep('review');

    const errors: string[] = [];

    for (let i = 0; i < queuedChanges.length; i++) {
      const change = queuedChanges[i];
      setSubmissionProgress({ current: i + 1, total: queuedChanges.length });

      try {
        const dto: CreateChangeRequestDto = {
          fieldName: change.fieldName,
          requestedValue: change.newValue,
          reason: change.reason,
        };
        await createChangeRequest(dto);
      } catch (err) {
        const fieldLabel = FIELD_LABELS[change.fieldName];
        const errorMsg = err instanceof Error ? err.message : 'Erreur inconnue';
        errors.push(`${fieldLabel}: ${errorMsg}`);
      }
    }

    setIsLoading(false);
    setSubmissionErrors(errors);

    // Always go to success step to show results (success or errors)
    setStep('success');

    if (errors.length === 0) {
      // All succeeded - auto close after delay
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 2000);
    } else if (errors.length < queuedChanges.length) {
      // Some succeeded, some failed - don't auto close, let user see the errors
      onSuccess?.();
    }
    // If all failed, just show the errors (no onSuccess call)
  };

  const handleClose = () => {
    setStep('select');
    setSelectedField(null);
    setNewValue('');
    setReason('');
    setError(null);
    setQueuedChanges([]);
    setSubmissionProgress({ current: 0, total: 0 });
    setSubmissionErrors([]);
    onClose();
  };

  const handleBack = () => {
    setSelectedField(null);
    setNewValue('');
    setReason('');
    setError(null);
    setStep('select');
  };

  // Get options for nomenclature fields
  const getNomenclatureOptions = (type: string): { value: string; label: string }[] => {
    switch (type) {
      case 'departements':
        return departements.map(d => ({
          value: d.id,
          label: d.labelFr,
        }));
      case 'grades':
        return grades.map(g => ({
          value: g.id,
          label: g.labelFr,
        }));
      case 'diplomes':
        return diplomes.map(d => ({
          value: d.labelFr,
          label: d.labelFr,
        }));
      case 'countries':
        return countries.map(c => ({
          value: c.nameFr,
          label: c.nameFr,
        }));
      default:
        return [];
    }
  };

  // Render the appropriate input based on field configuration
  const renderInput = () => {
    if (!selectedField) return null;

    const config = fieldConfigs[selectedField] || { inputType: 'text' };
    const inputClassName = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

    switch (config.inputType) {
      case 'select':
        return (
          <select
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
          >
            <option value="">Sélectionner...</option>
            {config.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'nomenclature':
        if (isLoadingNomenclature) {
          return (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-primary-600 animate-spin mr-2" />
              <span className="text-gray-500">Chargement des options...</span>
            </div>
          );
        }
        const nomenclatureOptions = getNomenclatureOptions(config.nomenclatureType || '');
        return (
          <select
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
          >
            <option value="">Sélectionner...</option>
            {nomenclatureOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            min={config.min}
            max={config.max}
            className={inputClassName}
            placeholder={config.placeholder}
          />
        );

      case 'tel':
        return (
          <input
            type="tel"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
            placeholder={config.placeholder || '+212 6XX XXX XXX'}
          />
        );

      case 'arabic':
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className={`${inputClassName} text-right`}
              placeholder={config.placeholder || 'أدخل النص بالعربية'}
              dir="rtl"
              lang="ar"
            />
            {/* Arabic Virtual Keyboard */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 mb-2 text-center">لوحة المفاتيح العربية</p>
              <div className="grid grid-cols-11 gap-1">
                {/* Row 1 */}
                {['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'].map((char) => (
                  <button
                    key={char}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Row 2 */}
                {['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ة'].map((char) => (
                  <button
                    key={char}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Row 3 */}
                {['ئ', 'ء', 'ؤ', 'ر', 'ى', 'ة', 'و', 'ز', 'ظ', 'ط', 'ذ'].map((char, idx) => (
                  <button
                    key={`${char}-${idx}`}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Row 4 - Special characters */}
                {['د', 'ـ', 'إ', 'أ', 'آ', 'ٱ'].map((char) => (
                  <button
                    key={char}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Space button */}
                <button
                  type="button"
                  onClick={() => setNewValue(prev => prev + ' ')}
                  className="col-span-3 p-2 text-sm bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                >
                  مسافة
                </button>
                {/* Backspace button */}
                <button
                  type="button"
                  onClick={() => setNewValue(prev => prev.slice(0, -1))}
                  className="col-span-2 p-2 text-sm bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors text-red-600"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        );

      case 'phone_with_country':
        return (
          <div className="flex">
            <div className="relative">
              <select
                value={selectedPhoneCountry}
                onChange={(e) => setSelectedPhoneCountry(e.target.value)}
                className="appearance-none px-3 py-3 pr-8 border border-gray-300 rounded-l-lg bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[120px]"
              >
                {COUNTRY_PHONE_CODES.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.dialCode}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <input
              type="tel"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className="flex-1 px-4 py-3 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={config.placeholder || '6XX XXX XXX'}
            />
          </div>
        );

      case 'document':
        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                Téléchargez le nouveau document. Après approbation par l'administration,
                il remplacera le document actuel dans votre profil.
              </p>
            </div>

            {/* File upload area */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Nouveau document <span className="text-red-500">*</span>
              </label>

              {!selectedFile ? (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 hover:bg-primary-50/30 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-1">
                    Cliquez pour sélectionner un fichier
                  </p>
                  <p className="text-xs text-gray-400">
                    PDF, JPG, PNG (max 5 MB)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          setError('Le fichier est trop volumineux (max 5 MB)');
                          return;
                        }
                        setSelectedFile(file);
                        setFilePreviewUrl(URL.createObjectURL(file));
                        setError(null);
                      }
                    }}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <FileText className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {filePreviewUrl && (
                        <button
                          type="button"
                          onClick={() => window.open(filePreviewUrl, '_blank')}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Aperçu"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          if (filePreviewUrl) {
                            URL.revokeObjectURL(filePreviewUrl);
                            setFilePreviewUrl(null);
                          }
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Optional reason/comment */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Commentaire (optionnel)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className={`${inputClassName} resize-none`}
                placeholder="Raison de la modification du document..."
              />
            </div>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
            placeholder={config.placeholder || 'Entrez la nouvelle valeur'}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Edit3 className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Demander une modification
              </h2>
              <p className="text-sm text-gray-500">
                {step === 'select'
                  ? 'Sélectionnez les champs à modifier'
                  : step === 'form'
                  ? FIELD_LABELS[selectedField!]
                  : step === 'review'
                  ? 'Envoi des demandes...'
                  : 'Demandes envoyées'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'success' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {submissionErrors.length === 0
                  ? 'Demandes envoyées'
                  : `${queuedChanges.length - submissionErrors.length} demande(s) envoyée(s)`}
              </h3>
              <p className="text-gray-500 text-center mb-4">
                {submissionErrors.length === 0
                  ? 'Vos demandes de modification ont été soumises. Vous serez notifié une fois qu\'elles auront été traitées.'
                  : 'Certaines demandes ont été soumises avec succès.'}
              </p>
              {submissionErrors.length > 0 && (
                <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-2">
                    {submissionErrors.length} erreur(s):
                  </p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {submissionErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Show close button when there are errors or after viewing success */}
              {submissionErrors.length > 0 && (
                <button
                  onClick={handleClose}
                  className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Fermer
                </button>
              )}
            </div>
          ) : step === 'review' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Envoi des demandes...
              </h3>
              <p className="text-gray-500">
                {submissionProgress.current} / {submissionProgress.total}
              </p>
              <div className="w-full max-w-xs mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${(submissionProgress.current / submissionProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ) : step === 'select' ? (
            <>
              {/* Queued changes summary */}
              {queuedChanges.length > 0 && (
                <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-primary-800">
                      Modifications à soumettre ({queuedChanges.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {queuedChanges.map((change) => (
                      <div
                        key={change.id}
                        className="flex items-center justify-between p-2 bg-white rounded-lg border border-primary-100"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {FIELD_LABELS[change.fieldName]}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {getDisplayValue(change.fieldName, change.currentValue)} <ArrowRight className="inline w-3 h-3" /> {getDisplayValue(change.fieldName, change.newValue)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveFromQueue(change.id)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isLoadingRequests ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {fieldGroups.map((group) => {
                    // Filter to only show fields that are applicable
                    const groupFields = group.fields.filter(f => applicableFields.includes(f));
                    if (groupFields.length === 0) return null;

                    return (
                      <div key={group.title}>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">{group.title}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {groupFields.map((field) => {
                            const isPending = hasPendingRequest(field);
                            const isQueued = isFieldQueued(field);
                            const currentValue = getCurrentValue(field);
                            const displayValue = getDisplayValue(field, currentValue);
                            return (
                              <button
                                key={field}
                                onClick={() => handleFieldSelect(field)}
                                disabled={isPending || isQueued}
                                className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors ${
                                  isPending
                                    ? 'border-orange-200 bg-orange-50 cursor-not-allowed'
                                    : isQueued
                                    ? 'border-primary-200 bg-primary-50 cursor-not-allowed'
                                    : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                                }`}
                              >
                                <span className="text-sm font-medium text-gray-900">
                                  {FIELD_LABELS[field]}
                                </span>
                                <span className="text-xs text-gray-500 truncate max-w-full">
                                  {displayValue}
                                </span>
                                {isPending && (
                                  <span className="text-xs text-orange-600 mt-1">
                                    Demande en attente
                                  </span>
                                )}
                                {isQueued && (
                                  <span className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                                    <Plus className="w-3 h-3" /> Ajouté
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Current value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valeur actuelle
                </label>
                <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-600">
                  {getDisplayValue(selectedField!, getCurrentValue(selectedField!))}
                </div>
              </div>

              {/* New value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouvelle valeur <span className="text-red-500">*</span>
                </label>
                {renderInput()}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison de la modification (optionnel)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  placeholder="Expliquez pourquoi vous souhaitez modifier cette information..."
                />
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  Votre demande sera examinée par l'administration. Vous serez notifié une fois la décision prise.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'success' && step !== 'review' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            {step === 'form' ? (
              <>
                <button
                  onClick={handleBack}
                  disabled={isUploadingDocument}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50 transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleAddToQueue}
                  disabled={
                    isUploadingDocument ||
                    (fieldConfigs[selectedField!]?.inputType === 'document' ? !selectedFile : !newValue.trim())
                  }
                  className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isUploadingDocument ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Téléchargement...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Ajouter à la liste
                    </>
                  )}
                </button>
              </>
            ) : (
              <div className="flex items-center justify-between w-full">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Fermer
                </button>
                {queuedChanges.length > 0 && (
                  <button
                    onClick={handleSubmitAll}
                    className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Soumettre {queuedChanges.length} demande{queuedChanges.length > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffChangeRequestModal;
